import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Shield, Search, Check, X } from 'lucide-react';
import type { User } from '@/shared/types';
import { Input } from '@travellegacy/nexops-design-system';
import { PermissionsModal } from '../components/PermissionsModal';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Permission, PERMISSION_LABELS, type UserPermissions } from '@/shared/types/permissions';

export default function PermissionsOverview() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersAPI.list();
      return response.data.data as User[];
    },
  });

  // Filtrar apenas GESTOR e COLLABORATOR
  const permissionUsers = useMemo(() => {
    if (!data) return [];

    let filtered = data.filter(
      (user) => user.role === 'GESTOR' || user.role === 'COLLABORATOR'
    );

    // Busca por nome ou email
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [data, searchQuery]);

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
    setIsPermissionsModalOpen(true);
  };

  const getPermissionBadge = (hasPermission: boolean) => {
    return hasPermission ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <X className="h-4 w-4 text-gray-400" />
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            Visão Geral de Permissões
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            Visualize e gerencie permissões de todos os colaboradores
          </p>
        </div>
      </div>

      <PermissionsModal
        isOpen={isPermissionsModalOpen}
        onClose={() => {
          setIsPermissionsModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Matriz de Permissões</span>
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {permissionUsers.length === 0 && !isLoading && (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">
                {searchQuery
                  ? 'Nenhum colaborador encontrado com os filtros aplicados'
                  : 'Nenhum colaborador encontrado'}
              </p>
            </div>
          )}

          {permissionUsers.length > 0 && (
            <div className="-mx-4 sm:mx-0 overflow-x-auto">
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="py-3 px-2 sm:px-4 text-xs sm:text-sm sticky left-0 bg-card z-10">
                      Usuário
                    </TableHead>
                    <TableHead className="py-3 px-2 sm:px-4 text-xs sm:text-sm">Função</TableHead>
                    {Object.values(Permission).map((permission) => (
                      <TableHead
                        key={permission}
                        className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-center"
                      >
                        <div className="flex flex-col items-center">
                          <span className="text-xs">{PERMISSION_LABELS[permission]}</span>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissionUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="cursor-pointer hover:bg-accent/50"
                      onClick={() => handleUserClick(user)}
                    >
                      <TableCell className="py-3 px-2 sm:px-4 sticky left-0 bg-card z-10">
                        <div>
                          <div className="font-medium text-foreground text-sm">{user.name}</div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 px-2 sm:px-4">
                        <Badge
                          className={
                            user.role === 'GESTOR'
                              ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                              : 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'
                          }
                        >
                          {user.role}
                        </Badge>
                      </TableCell>
                      {Object.values(Permission).map((permission) => (
                        <TableCell key={permission} className="py-3 px-2 sm:px-4 text-center">
                          {getPermissionBadge(
                            (user.permissions as UserPermissions)?.[permission] ?? false
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
